import { toUtcAdjusted } from '@noor/lib';
import type { CourierListSearch } from '../model/list-params';
import { COURIER_SORT_FIELDS, type CourierSortField } from './labels';

/**
 * Wire filter for `GET admin/couriers` (ported 1:1 from cpanel
 * `pages/couriers/index.jsx:courierListFilter`, including the UTC+5 date
 * adjustment). The only deliberate change: pagination arrives as
 * page/pageSize and is converted to the wire `limit`/`skip` here.
 */
export interface CouriersListFilter {
  limit: number;
  skip: number;
  search?: string | undefined;
  group?: string | undefined;
  min_rating?: number | undefined;
  status?: string | undefined;
  equipment_id?: string | undefined;
  balance_from?: string | undefined;
  balance_till?: string | undefined;
  scooter_balance_from?: string | undefined;
  scooter_balance_till?: string | undefined;
  has_activity?: boolean | undefined;
  has_scooter?: boolean | undefined;
  orders_from_date?: string | undefined;
  orders_till_date?: string | undefined;
  order_count_from?: string | undefined;
  order_count_till?: string | undefined;
  rating_from?: string | undefined;
  rating_till?: string | undefined;
  order_by_rating?: boolean | undefined;
  order_by_balance?: boolean | undefined;
  order_by_created_at?: boolean | undefined;
  order_by_order_price?: boolean | undefined;
  order_by_scooter_balance?: boolean | undefined;
  order_by_order_count?: boolean | undefined;
  order_by_average_cheque?: boolean | undefined;
  last_update_status?: boolean | undefined;
}

export interface CourierSort {
  field: CourierSortField;
  /** 1 = ascending (wire `true`), 2 = descending (wire `false`). */
  position: 1 | 2;
}

/** Parse the URL `sort` value; unknown fields/positions mean "no sort". */
export function parseCourierSort(raw: string | undefined): CourierSort | null {
  if (!raw) return null;
  const [field, position] = raw.split(':');
  if (!(COURIER_SORT_FIELDS as readonly string[]).includes(field ?? '')) return null;
  if (position !== '1' && position !== '2') return null;
  return { field: field as CourierSortField, position: Number(position) as 1 | 2 };
}

export function serializeCourierSort(sort: CourierSort | null): string | undefined {
  return sort ? `${sort.field}:${sort.position}` : undefined;
}

/**
 * Header click cycle (cpanel `CustomHeader`): none → 1 → 2 → none, and
 * choosing a field clears every other field (single active sort).
 */
export function nextCourierSort(
  current: CourierSort | null,
  field: CourierSortField,
): CourierSort | null {
  if (current?.field !== field) return { field, position: 1 };
  if (current.position === 1) return { field, position: 2 };
  return null;
}

const SORT_WIRE_KEY = {
  average_rating: 'order_by_rating',
  balance: 'order_by_balance',
  created_date: 'order_by_created_at',
  // Legacy quirk kept: the "sum of all orders" column sorts by order price.
  last_order_date: 'order_by_order_price',
  scooter_debt: 'order_by_scooter_balance',
  orders_count: 'order_by_order_count',
  average_cheque: 'order_by_average_cheque',
  status: 'last_update_status',
} as const satisfies Record<CourierSortField, keyof CouriersListFilter>;

export function buildCouriersListFilter(args: {
  values: Partial<CourierListSearch>;
  sort: CourierSort | null;
}): CouriersListFilter {
  const { values, sort } = args;
  const page = values.page ?? 1;
  const pageSize = values.pageSize ?? 10;
  const filter: CouriersListFilter = {
    limit: pageSize,
    skip: (page - 1) * pageSize,
    search: values.search,
    group: values.group_id,
    min_rating: values.rating ? Number(values.rating) : undefined,
  };

  if (values.status && values.status !== 'ALL') filter.status = values.status;
  if (values.equipment) filter.equipment_id = values.equipment;
  if (values.balance_from) filter.balance_from = values.balance_from;
  if (values.balance_till) filter.balance_till = values.balance_till;
  if (values.scooter_balance_from) {
    filter.scooter_balance_from = values.scooter_balance_from;
  }
  if (values.scooter_balance_till) {
    filter.scooter_balance_till = values.scooter_balance_till;
  }

  // Legacy quirk kept: BOTH flags are sent for any activity choice, so
  // `has_activity` also forces `has_scooter=false` (and vice versa).
  if (values.activity) {
    filter.has_activity =
      values.activity === 'no_activity' ? false : values.activity === 'has_activity';
    filter.has_scooter =
      values.activity === 'no_scooter' ? false : values.activity === 'has_scooter';
  }

  if (values.start_date) {
    filter.orders_from_date = toUtcAdjusted(values.start_date).toISOString();
  }
  if (values.end_date) {
    filter.orders_till_date = toUtcAdjusted(values.end_date).add(1, 'day').toISOString();
  }
  if (values.orders_count_from) filter.order_count_from = values.orders_count_from;
  if (values.orders_count_till) filter.order_count_till = values.orders_count_till;
  if (values.rating_from) filter.rating_from = values.rating_from;
  if (values.rating_till) filter.rating_till = values.rating_till;

  if (sort) {
    filter[SORT_WIRE_KEY[sort.field]] = sort.position === 1;
  }
  return filter;
}
